/**
 * Follow-Up Configuration Hook
 *
 * React hook for managing follow-up notification settings.
 * Follows the useGmailAccounts pattern.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  getFollowUpConfig,
  saveFollowUpConfig,
  testFollowUp,
  getFollowUpActivity,
} from '../api/followUps';
import { ApiClientError } from '../api/client';
import type { FollowUpConfig, FollowUpRule, FollowUpSent } from '../types';

// ============================================
// Follow-Up Config Hook
// ============================================

interface UseFollowUpConfigResult {
  config: FollowUpConfig | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  saveConfig: (updates: Partial<FollowUpConfig>) => Promise<boolean>;
  toggleRule: (ruleId: string, enabled: boolean) => Promise<boolean>;
  saveRule: (rule: FollowUpRule) => Promise<boolean>;
  sendTest: (ruleId: string, orderId: string) => Promise<{ success: boolean; message?: string; error?: string }>;
  actionLoading: boolean;
}

export function useFollowUpConfig(): UseFollowUpConfigResult {
  const [config, setConfig] = useState<FollowUpConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getFollowUpConfig();
      setConfig(response);
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Failed to fetch follow-up config';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const saveConfigHandler = useCallback(
    async (updates: Partial<FollowUpConfig>): Promise<boolean> => {
      setActionLoading(true);
      setError(null);
      try {
        const response = await saveFollowUpConfig(updates);
        setConfig(response);
        return true;
      } catch (err) {
        const message = err instanceof ApiClientError ? err.message : 'Failed to save config';
        setError(message);
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    []
  );

  const toggleRule = useCallback(
    async (ruleId: string, enabled: boolean): Promise<boolean> => {
      if (!config) return false;
      setActionLoading(true);
      setError(null);
      try {
        const updatedRules = config.rules.map((r) =>
          r.id === ruleId ? { ...r, enabled } : r
        );
        const response = await saveFollowUpConfig({ rules: updatedRules });
        setConfig(response);
        return true;
      } catch (err) {
        const message = err instanceof ApiClientError ? err.message : 'Failed to toggle rule';
        setError(message);
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    [config]
  );

  const saveRule = useCallback(
    async (rule: FollowUpRule): Promise<boolean> => {
      if (!config) return false;
      setActionLoading(true);
      setError(null);
      try {
        const updatedRules = config.rules.map((r) =>
          r.id === rule.id ? rule : r
        );
        const response = await saveFollowUpConfig({ rules: updatedRules });
        setConfig(response);
        return true;
      } catch (err) {
        const message = err instanceof ApiClientError ? err.message : 'Failed to save rule';
        setError(message);
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    [config]
  );

  const sendTest = useCallback(
    async (ruleId: string, orderId: string) => {
      setActionLoading(true);
      setError(null);
      try {
        const result = await testFollowUp(ruleId, orderId);
        return result;
      } catch (err) {
        const message = err instanceof ApiClientError ? err.message : 'Failed to send test';
        setError(message);
        return { success: false, error: message };
      } finally {
        setActionLoading(false);
      }
    },
    []
  );

  return {
    config,
    loading,
    error,
    refetch: fetchConfig,
    saveConfig: saveConfigHandler,
    toggleRule,
    saveRule,
    sendTest,
    actionLoading,
  };
}

// ============================================
// Follow-Up Activity Hook
// ============================================

interface UseFollowUpActivityResult {
  activity: FollowUpSent[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  hasMore: boolean;
  loadMore: () => Promise<void>;
}

export function useFollowUpActivity(): UseFollowUpActivityResult {
  const [activity, setActivity] = useState<FollowUpSent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const fetchActivity = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getFollowUpActivity(1, 20);
      setActivity(response.activity);
      setHasMore(response.hasMore);
      setPage(1);
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Failed to fetch activity';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;
    setLoading(true);
    try {
      const nextPage = page + 1;
      const response = await getFollowUpActivity(nextPage, 20);
      setActivity((prev) => [...prev, ...response.activity]);
      setHasMore(response.hasMore);
      setPage(nextPage);
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Failed to load more';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [hasMore, loading, page]);

  return {
    activity,
    loading,
    error,
    refetch: fetchActivity,
    hasMore,
    loadMore,
  };
}
